import React, { useState } from 'react';
import { Table, Card, Typography, Button, Modal, Form, Input, Select, Space, Popconfirm, message, Switch } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { keywordApi } from '../api';
import { Keyword, KeywordCreateRequest, BatchKeywordCreateRequest } from '../types';

const { TextArea } = Input;

const { Title } = Typography;
const { Option } = Select;

const Keywords: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState<Keyword | null>(null);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  // 获取关键词列表
  const { data: keywords, isLoading } = useQuery(
    'keywords',
    () => keywordApi.getKeywords(),
  );

  // 获取分类列表
  const { data: categories } = useQuery(
    'categories',
    () => keywordApi.getCategories(),
  );

  // 创建关键词
  const createMutation = useMutation(
    (data: BatchKeywordCreateRequest) => keywordApi.createBatchKeywords(data),
    {
      onSuccess: (response) => {
        const { success_count, failed_count, errors } = response.data;
        if (failed_count === 0) {
          message.success(`成功创建 ${success_count} 个关键词`);
        } else {
          message.warning(`成功创建 ${success_count} 个关键词，失败 ${failed_count} 个`);
          if (errors.length > 0) {
            console.error('创建失败的关键词:', errors);
          }
        }
        queryClient.invalidateQueries('keywords');
        setIsModalVisible(false);
        form.resetFields();
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.detail || '关键词创建失败';
        message.error(errorMessage);
      },
    }
  );

  // 更新关键词
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<KeywordCreateRequest & { status: string }> }) =>
      keywordApi.updateKeyword(id, data),
    {
      onSuccess: () => {
        message.success('关键词更新成功');
        queryClient.invalidateQueries('keywords');
        setIsModalVisible(false);
        setEditingKeyword(null);
        form.resetFields();
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.detail || '关键词更新失败';
        message.error(errorMessage);
      },
    }
  );

  // 删除关键词
  const deleteMutation = useMutation(
    (id: string) => keywordApi.deleteKeyword(id),
    {
      onSuccess: () => {
        message.success('关键词删除成功');
        queryClient.invalidateQueries('keywords');
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.detail || '关键词删除失败';
        message.error(errorMessage);
      },
    }
  );

  // 切换关键词状态
  const toggleStatusMutation = useMutation(
    ({ id, status }: { id: string; status: string }) => 
      keywordApi.updateKeyword(id, { status }),
    {
      onSuccess: () => {
        message.success('状态更新成功');
        queryClient.invalidateQueries('keywords');
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.detail || '状态更新失败';
        message.error(errorMessage);
      },
    }
  );

  const handleAdd = () => {
    setEditingKeyword(null);
    setIsAddingNewCategory(false);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleCategoryChange = (value: string) => {
    if (value === 'new') {
      setIsAddingNewCategory(true);
      form.setFieldsValue({ primary_category: '' });
    } else {
      setIsAddingNewCategory(false);
      form.setFieldsValue({ primary_category: value });
    }
  };

  const handleEdit = (record: Keyword) => {
    setEditingKeyword(record);
    setIsAddingNewCategory(false);
    setIsModalVisible(true);
    form.setFieldsValue({
      primary_category: record.primary_category,
      keyword: record.keyword,
    });
  };

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  const handleStatusToggle = (record: Keyword) => {
    const newStatus = record.status === 'active' ? 'inactive' : 'active';
    toggleStatusMutation.mutate({ id: record.id, status: newStatus });
  };


  const handleSubmit = (values: KeywordCreateRequest) => {
    if (editingKeyword) {
      updateMutation.mutate({ id: editingKeyword.id, data: values });
    } else {
      // 批量创建关键词
      const batchData: BatchKeywordCreateRequest = {
        primary_category: values.primary_category,
        keywords: values.keyword
      };
      createMutation.mutate(batchData);
    }
  };

  const columns = [
    {
      title: '一级分类',
      dataIndex: 'primary_category',
      key: 'primary_category',
    },
    {
      title: '关键词',
      dataIndex: 'keyword',
      key: 'keyword',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: Keyword) => (
        <Switch
          checked={status === 'active'}
          onChange={() => handleStatusToggle(record)}
          loading={toggleStatusMutation.isLoading}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time: string) => new Date(time).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Keyword) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个关键词吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>关键词管理</Title>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAdd}
        >
          添加关键词
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={keywords?.data}
          loading={isLoading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </Card>

      <Modal
        title={editingKeyword ? '编辑关键词' : '添加关键词'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingKeyword(null);
          setIsAddingNewCategory(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="primary_category"
            label="一级分类"
            rules={[{ required: true, message: '请选择或输入一级分类' }]}
          >
            {isAddingNewCategory ? (
              <div>
                <Input 
                  placeholder="请输入新分类名称"
                  onChange={(e) => form.setFieldsValue({ primary_category: e.target.value })}
                />
                <Button 
                  type="link" 
                  size="small" 
                  onClick={() => {
                    setIsAddingNewCategory(false);
                    form.setFieldsValue({ primary_category: '' });
                  }}
                  style={{ padding: 0, marginTop: 4 }}
                >
                  ← 返回选择现有分类
                </Button>
              </div>
            ) : (
              <Select 
                placeholder="请选择分类"
                showSearch
                optionFilterProp="children"
                onChange={handleCategoryChange}
              >
                {categories?.data.map((category: string) => (
                  <Option key={category} value={category}>{category}</Option>
                ))}
                <Option value="new">+ 添加新分类</Option>
              </Select>
            )}
          </Form.Item>

          <Form.Item
            name="keyword"
            label="关键词"
            rules={[{ required: true, message: '请输入关键词' }]}
          >
            <TextArea 
              placeholder="请输入关键词，多个关键词请换行分隔"
              rows={4}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={createMutation.isLoading || updateMutation.isLoading}
              >
                {editingKeyword ? '更新' : '创建'}
              </Button>
              <Button onClick={() => setIsModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Keywords; 